import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import { LEAD_STATUS_MAP } from '../../utils/constants';
import Modal from '../../components/common/Modal';

const LEAD_SOURCES = ['walk-in', 'referral', 'field', 'phone', 'social-media', 'campaign', 'other'];

// ═══════════════════════════════════════════════════════════
// FIX: Extract a string ID from a value that might be either
// a plain ObjectId string OR a populated Mongoose object.
// ═══════════════════════════════════════════════════════════
const extractId = (val) => {
  if (!val) return null;
  if (typeof val === 'object' && val._id) return val._id;
  if (typeof val === 'string') return val;
  return null;
};

// Helper to check if user is Admin
const getIsAdmin = () => {
  try {
    const userStr = localStorage.getItem('user') || localStorage.getItem('authUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user?.role?.name === 'Admin';
    }
  } catch {}
  return false;
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Status change state
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Reconvert state
  const [reconverting, setReconverting] = useState(false);

  // Lead interactions
  const [interactions, setInteractions] = useState([]);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [interactionSaving, setInteractionSaving] = useState(false);
  const [interactionForm, setInteractionForm] = useState({ type: 'call', content: '' });
  const isAdmin = getIsAdmin();

  useEffect(() => {
    fetchLead();
    fetchInteractions();
  }, [id]);

  const fetchInteractions = async () => {
    try {
      const res = await api.get(`/leads/${id}/interactions`);
      setInteractions(res.data.data || []);
    } catch (err) {
      console.error('Failed to load lead interactions', err);
    }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    if (!interactionForm.content.trim()) return;
    setInteractionSaving(true);
    try {
      await api.post(`/leads/${id}/interactions`, {
        step: 0,
        stepTitle: 'Lead Follow-up',
        type: interactionForm.type,
        content: interactionForm.content.trim(),
      });
      setInteractionForm({ type: 'call', content: '' });
      setShowInteractionModal(false);
      await fetchInteractions();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save interaction');
    } finally {
      setInteractionSaving(false);
    }
  };

  const fetchLead = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/leads/${id}`);
      setLead(res.data.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Lead not found');
      } else {
        setError('Failed to load lead details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === lead.status) return;

    if (newStatus === 'converted' || newStatus === 'onboarding') {
      navigate(`/clients/onboard/${lead._id}`);
      return;
    }

    if (lead.status === 'onboarding') {
      const confirmMsg = `⚠️ WARNING: This lead is currently in the onboarding process. Changing the status will abandon their progress. Are you sure?`;
      if (!window.confirm(confirmMsg)) {
        e.target.value = lead.status;
        return;
      }
    } else {
      const confirmMsg = `Change status from "${lead.status}" to "${newStatus}"?`;
      if (!window.confirm(confirmMsg)) {
        e.target.value = lead.status;
        return;
      }
    }

    setChangingStatus(true);
    try {
      const res = await api.patch(`/leads/${id}/status`, { status: newStatus });
      setLead(res.data.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleConvertToClient = () => {
    navigate(`/clients/onboard/${lead._id}`);
  };

  const handleReconvertToLead = async () => {
    const confirmMsg = `⚠️ WARNING: This will permanently delete the client record, all their onboarding documents, and revert this lead back to "qualified".\n\nThis cannot be undone. Are you sure?`;
    if (!window.confirm(confirmMsg)) return;

    setReconverting(true);
    try {
      const res = await api.post(`/leads/${id}/reconvert`);
      setLead(res.data.data);
      alert('Successfully reverted back to Lead. You can now convert them again.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reconvert to lead');
    } finally {
      setReconverting(false);
    }
  };

  // Edit handlers
  const openEditModal = () => {
    setEditForm({
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      phone: lead.phone || '',
      email: lead.email || '',
      nationalId: '',
      address: lead.address || '',
      source: lead.source || 'walk-in',
      followUpDate: lead.followUpDate ? lead.followUpDate.split('T')[0] : '',
      notes: lead.notes || '',
    });
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    try {
      const payload = { ...editForm };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '' && key !== 'firstName' && key !== 'lastName' && key !== 'phone') {
          delete payload[key];
        }
      });
      if (!payload.nationalId) delete payload.nationalId;

      const res = await api.put(`/leads/${id}`, payload);
      setLead(res.data.data);
      setShowEditModal(false);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update lead.';
      if (err.response?.data?.errors) {
        setEditError(err.response.data.errors.map((e) => e.message).join(', '));
      } else {
        setEditError(msg);
      }
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/leads/${id}`);
      navigate('/leads');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete lead');
    }
  };

  if (loading) return <div className="spinner"></div>;

  if (error) {
    return (
      <div>
        <button className="btn btn-outline btn-sm mb-2" onClick={() => navigate('/leads')}>
          ← Back to Leads
        </button>
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: 'var(--danger)', fontSize: 16 }}>{error}</p>
          <button className="btn btn-primary mt-2" onClick={fetchLead}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  // ═══════════════════════════════════════════════════════════
  // FIX: Extract the string ID from convertedToClient which
  // might be a populated object or a raw string
  // ═══════════════════════════════════════════════════════════
  const convertedToClientId = extractId(lead.convertedToClient);

  const isOverdueFollowUp =
    lead.followUpDate &&
    new Date(lead.followUpDate) <= new Date() &&
    !['converted', 'onboarding', 'lost', 'unqualified'].includes(lead.status);

  return (
    <div>
      <button className="btn btn-outline btn-sm mb-2" onClick={() => navigate('/leads')}>
        ← Back to Leads
      </button>

      {/* Header Card */}
      <div className="card">
        <div className="flex justify-between items-center mb-2" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ marginBottom: 4 }}>
              {lead.firstName} {lead.lastName}
            </h3>
            <p className="text-muted text-xs">
              Created {new Date(lead.createdAt).toLocaleDateString()} · Last updated {new Date(lead.updatedAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-1" style={{ flexWrap: 'wrap' }}>
            <StatusBadge status={lead.status} map={LEAD_STATUS_MAP} />
            <select
              className="form-control"
              style={{ width: 'auto' }}
              value={lead.status}
              onChange={handleStatusChange}
              disabled={changingStatus || lead.status === 'converted'}
            >
              {Object.keys(LEAD_STATUS_MAP)
                .filter((s) => s !== 'converted')
                .map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
            </select>
            <button className="btn btn-outline btn-sm" onClick={openEditModal}>
              ✏️ Edit
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete}>
              🗑️ Delete
            </button>
          </div>
        </div>

        {/* Detail Grid */}
        <div className="grid grid-2 gap-2" style={{ marginTop: 16 }}>
          <div>
            <p className="text-muted text-xs">Phone</p>
            <p style={{ fontWeight: 500 }}>{lead.phone}</p>
          </div>
          <div>
            <p className="text-muted text-xs">Email</p>
            <p>{lead.email || 'N/A'}</p>
          </div>
          <div>
            <p className="text-muted text-xs">Source</p>
            <p style={{ textTransform: 'capitalize' }}>{lead.source?.replace('-', ' ') || 'N/A'}</p>
          </div>
          <div>
            <p className="text-muted text-xs">Address</p>
            <p>{lead.address || 'N/A'}</p>
          </div>
          <div>
            <p className="text-muted text-xs">Assigned To</p>
            <p>
              {lead.assignedTo
                ? `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}`
                : 'Unassigned'}
            </p>
          </div>
          <div>
            <p className="text-muted text-xs">Branch</p>
            <p>{lead.branch ? (typeof lead.branch === 'object' ? lead.branch.name : lead.branch) : 'N/A'}</p>
          </div>
          <div>
            <p className="text-muted text-xs">Follow-up Date</p>
            <p style={isOverdueFollowUp ? { color: 'var(--danger)', fontWeight: 600 } : {}}>
              {lead.followUpDate
                ? new Date(lead.followUpDate).toLocaleDateString()
                : 'Not set'}
              {isOverdueFollowUp && ' ⚠️ Overdue'}
            </p>
          </div>
          <div>
            <p className="text-muted text-xs">Status</p>
            <StatusBadge status={lead.status} map={LEAD_STATUS_MAP} />
          </div>
        </div>

        {/* Notes */}
        {lead.notes && (
          <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <p className="text-muted text-xs" style={{ marginBottom: 4 }}>Notes</p>
            <p style={{ whiteSpace: 'pre-wrap' }}>{lead.notes}</p>
          </div>
        )}

        {/* LEAD INTERACTIONS */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div className="flex justify-between items-center" style={{ gap: 10, marginBottom: 12 }}>
            <div>
              <h4 style={{ margin: 0 }}>Interactions</h4>
              <p className="text-muted text-xs" style={{ margin: '3px 0 0' }}>Calls, visits, SMS, WhatsApp and notes recorded against this lead.</p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowInteractionModal(true)}>＋ Add Interaction</button>
          </div>
          {interactions.length === 0 ? (
            <div style={{ padding: 16, background: 'var(--bg-secondary)', borderRadius: 8, color: 'var(--text-muted)', fontSize: 13 }}>No interactions recorded yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {interactions.map((item) => (
                <div key={item._id} style={{ padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: 8, borderLeft: '3px solid var(--primary)' }}>
                  <div className="flex justify-between items-center" style={{ gap: 10 }}>
                    <strong style={{ textTransform: 'capitalize' }}>{item.type || 'note'}</strong>
                    <span className="text-muted text-xs">{item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}</span>
                  </div>
                  <p style={{ margin: '6px 0', whiteSpace: 'pre-wrap' }}>{item.content}</p>
                  <span className="text-muted text-xs">Recorded by {item.recordedBy ? `${item.recordedBy.firstName || ''} ${item.recordedBy.lastName || ''}`.trim() : 'Unknown'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ONBOARDING IN PROGRESS STATE */}
        {lead.status === 'onboarding' && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <p style={{ color: '#1e40af', fontWeight: 600, margin: 0 }}>🔄 Onboarding In Progress</p>
                <p className="text-xs text-muted" style={{ margin: '4px 0 0' }}>
                  This lead is currently going through the client onboarding process. It will become a fully converted client only after submission for approval.
                </p>
              </div>
              <button className="btn btn-primary" onClick={handleConvertToClient}>
                ▶️ Continue Onboarding
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            FIX: Use extracted convertedToClientId instead of
            lead.convertedToClient directly to avoid "[object Object]"
            ═══════════════════════════════════════════════════════════ */}
        {lead.status === 'converted' && convertedToClientId && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <p className="text-xs" style={{ color: '#15803d', fontWeight: 600, margin: 0 }}>✅ Converted to Client</p>
                <p className="text-xs text-muted" style={{ margin: '4px 0 0' }}>
                  Client ID: {convertedToClientId}
                </p>
              </div>
              
              {isAdmin && (
                <button 
                  className="btn btn-warning btn-sm" 
                  onClick={handleReconvertToLead} 
                  disabled={reconverting}
                >
                  {reconverting ? '⏳ Reverting...' : '🔄 Reconvert to Lead (Admin)'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* CONVERT TO CLIENT BUTTON */}
        {lead.status !== 'converted' && lead.status !== 'onboarding' && (
          <div style={{ marginTop: 20 }}>
            <button className="btn btn-success" onClick={handleConvertToClient}>
              🔄 Convert to Client
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={showInteractionModal} onClose={() => setShowInteractionModal(false)} title="Add Lead Interaction" size="520px">
        <form onSubmit={handleAddInteraction}>
          <div className="form-group">
            <label>Interaction Type</label>
            <select className="form-control" value={interactionForm.type} onChange={(e) => setInteractionForm(p => ({ ...p, type: e.target.value }))}>
              {['call', 'visit', 'sms', 'whatsapp', 'email', 'note', 'other'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Interaction Details *</label>
            <textarea className="form-control" rows="5" required value={interactionForm.content} onChange={(e) => setInteractionForm(p => ({ ...p, content: e.target.value }))} placeholder="Record what happened, the outcome and any next step..." />
          </div>
          <div className="flex gap-1 mt-2">
            <button type="submit" className="btn btn-primary" disabled={interactionSaving}>{interactionSaving ? 'Saving...' : 'Save Interaction'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowInteractionModal(false)}>Cancel</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Lead" size="600px">
        <form onSubmit={handleEditSubmit}>
          {editError && (
            <div style={{ color: 'red', marginBottom: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 6, fontSize: 14 }}>
              {editError}
            </div>
          )}
          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>First Name *</label>
              <input
                className="form-control"
                required
                maxLength={50}
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                className="form-control"
                required
                maxLength={50}
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>Phone *</label>
              <input
                className="form-control"
                required
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                className="form-control"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>National ID</label>
              <input
                className="form-control"
                value={editForm.nationalId}
                onChange={(e) => setEditForm({ ...editForm, nationalId: e.target.value })}
                placeholder="Leave blank to keep existing"
              />
            </div>
            <div className="form-group">
              <label>Source</label>
              <select
                className="form-control"
                value={editForm.source}
                onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
              >
                {LEAD_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Address</label>
            <input
              className="form-control"
              value={editForm.address}
              onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Follow-up Date</label>
            <input
              className="form-control"
              type="date"
              value={editForm.followUpDate}
              onChange={(e) => setEditForm({ ...editForm, followUpDate: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              className="form-control"
              rows="3"
              maxLength={2000}
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            />
          </div>
          <div className="flex gap-1 mt-2">
            <button type="submit" className="btn btn-primary" disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Update Lead'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}